import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { NewbornIPPSizeQuestionsLinkIds } from "@/encounters/constants/newborn-ipp";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";

const SizeDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.NewbornIPPSize,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const previousSizeAnswers = getQuestionnaireAnswers(previousVersion);
  const currentSizeAnswers = getQuestionnaireAnswers(currentVersion);

  const previousScale = previousSizeAnswers[NewbornIPPSizeQuestionsLinkIds.Scale]?.valueString;
  const currentScale = currentSizeAnswers[NewbornIPPSizeQuestionsLinkIds.Scale]?.valueString;
  const previousWeight = previousSizeAnswers[NewbornIPPSizeQuestionsLinkIds.Weight]?.valueDecimal;
  const currentWeight = currentSizeAnswers[NewbornIPPSizeQuestionsLinkIds.Weight]?.valueDecimal;
  const previousWeightOunces =
    previousSizeAnswers[NewbornIPPSizeQuestionsLinkIds.WeightOunces]?.valueDecimal;
  const currentWeightOunces =
    currentSizeAnswers[NewbornIPPSizeQuestionsLinkIds.WeightOunces]?.valueDecimal;
  const previousLength = previousSizeAnswers[NewbornIPPSizeQuestionsLinkIds.Length]?.valueDecimal;
  const currentLength = currentSizeAnswers[NewbornIPPSizeQuestionsLinkIds.Length]?.valueDecimal;
  const previousHeadCircumference =
    previousSizeAnswers[NewbornIPPSizeQuestionsLinkIds.HeadCircumference]?.valueDecimal;
  const currentHeadCircumference =
    currentSizeAnswers[NewbornIPPSizeQuestionsLinkIds.HeadCircumference]?.valueDecimal;
  const previousChestCircumference =
    previousSizeAnswers[NewbornIPPSizeQuestionsLinkIds.ChestCircumference]?.valueDecimal;
  const currentChestCircumference =
    currentSizeAnswers[NewbornIPPSizeQuestionsLinkIds.ChestCircumference]?.valueDecimal;
  const previousNotes = previousSizeAnswers[NewbornIPPSizeQuestionsLinkIds.Notes]?.valueString;
  const currentNotes = currentSizeAnswers[NewbornIPPSizeQuestionsLinkIds.Notes]?.valueString;

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Size Measurements</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Scale</TextWrapper>
                <WordsDiff showRemoved current={currentScale} previous={previousScale} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Weight</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentWeight?.toString()}
                  previous={previousWeight?.toString()}
                />
              </Stack>
              {(previousScale === "imperial" || currentScale === "imperial") && (
                <Stack gap="0">
                  <TextWrapper fw={600}>Weight (Ounces)</TextWrapper>
                  <WordsDiff
                    showRemoved
                    current={currentWeightOunces?.toString()}
                    previous={previousWeightOunces?.toString()}
                  />
                </Stack>
              )}
              <Stack gap="0">
                <TextWrapper fw={600}>Length</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentLength?.toString()}
                  previous={previousLength?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Head Circumference</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentHeadCircumference?.toString()}
                  previous={previousHeadCircumference?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Chest Circumference</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentChestCircumference?.toString()}
                  previous={previousChestCircumference?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Scale</TextWrapper>
                <WordsDiff current={currentScale} previous={previousScale} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Weight</TextWrapper>
                <WordsDiff
                  current={currentWeight?.toString()}
                  previous={previousWeight?.toString()}
                />
              </Stack>
              {(previousScale === "imperial" || currentScale === "imperial") && (
                <Stack gap="0">
                  <TextWrapper fw={600}>Weight (Ounces)</TextWrapper>
                  <WordsDiff
                    current={currentWeightOunces?.toString()}
                    previous={previousWeightOunces?.toString()}
                  />
                </Stack>
              )}
              <Stack gap="0">
                <TextWrapper fw={600}>Length</TextWrapper>
                <WordsDiff
                  current={currentLength?.toString()}
                  previous={previousLength?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Head Circumference</TextWrapper>
                <WordsDiff
                  current={currentHeadCircumference?.toString()}
                  previous={previousHeadCircumference?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Chest Circumference</TextWrapper>
                <WordsDiff
                  current={currentChestCircumference?.toString()}
                  previous={previousChestCircumference?.toString()}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default SizeDiff;
